import React, { useEffect, useState } from 'react';
import { 
  Table, Tag, Space, Button, message, 
  Card, Typography, Select, Modal,
  Avatar, Tooltip, Form, Input, Grid
} from 'antd';
import { 
  UserOutlined, 
  TeamOutlined, 
  SafetyCertificateOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { usersService } from '../../services/users.service';
import { User, UserRole } from '../../types';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { confirm } = Modal;
const { useBreakpoint } = Grid;

const ALL_PERMISSIONS = ['dashboard', 'pages', 'posts', 'auto-post'];

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await usersService.getAllUsers();
      setUsers(data);
    } catch (err) {
      message.error('Không thể tải danh sách người dùng');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      await usersService.updateUserRole(userId, newRole);
      if (newRole === UserRole.USER) {
        await usersService.updateUserPermissions(userId, ALL_PERMISSIONS);
        message.success('Đã cập nhật vai trò và tự động cấp toàn bộ quyền module');
      } else {
        message.success('Cập nhật vai trò thành công');
      }
      fetchUsers();
    } catch (err) {
      message.error('Không thể cập nhật vai trò');
    }
  };

  const handlePermissionsChange = async (userId: string, newPermissions: string[]) => {
    try {
      await usersService.updateUserPermissions(userId, newPermissions);
      message.success('Cập nhật quyền module thành công');
      fetchUsers();
    } catch (err) {
      message.error('Không thể cập nhật quyền module');
    }
  };

  const handleDelete = (userId: string) => {
    confirm({
      title: 'Bạn có chắc chắn muốn xóa người dùng này?',
      icon: <ExclamationCircleOutlined />,
      content: 'Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await usersService.adminDeleteUser(userId);
          message.success('Đã xóa người dùng thành công');
          fetchUsers();
        } catch (err) {
          message.error('Không thể xóa người dùng');
        }
      },
    });
  };

  const showModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      form.setFieldsValue({
        username: user.username,
        name: user.name,
        email: user.email,
        role: user.role,
        permissions: user.permissions,
        password: '', // Don't show password
      });
    } else {
      setEditingUser(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const { confirm, ...values } = await form.validateFields();
      if (editingUser) {
        // Prepare data (only include password if provided)
        const updateData = { ...values };
        if (!updateData.password) delete updateData.password;
        if (!updateData.email) delete updateData.email; // Don't send empty string/null for unique field
        
        await usersService.updateAnyUser(editingUser.id, updateData);
        message.success('Cập nhật thông tin thành công');
      } else {
        const createData = { ...values };
        if (!createData.email) delete createData.email; // Don't send empty string/null for unique field
        await usersService.createUser(createData);
        message.success('Thêm người dùng mới thành công');
      }
      setModalVisible(false);
      fetchUsers();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Có lỗi xảy ra khi lưu thông tin';
      message.error(errorMsg);
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'Người dùng',
      key: 'user',
      fixed: (isMobile ? 'left' : undefined) as any,
      width: isMobile ? 180 : 250,
      render: (_: any, record: User) => (
        <Space>
          <Avatar size={isMobile ? 'small' : 'default'} src={record.profilePicture} icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold', fontSize: isMobile ? 13 : 14 }}>{record.name}</div>
            <Text type="secondary" style={{ fontSize: '11px' }}>{record.username}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      key: 'role',
      width: 130,
      render: (role: UserRole, record: User) => (
        <Select
          size={isMobile ? 'small' : 'middle'}
          value={role || UserRole.USER}
          style={{ width: isMobile ? 100 : 120 }}
          onChange={(value) => handleRoleChange(record.id, value)}
        >
          <Select.Option value={UserRole.USER}>User</Select.Option>
          <Select.Option value={UserRole.ADMIN}>Admin</Select.Option>
        </Select>
      ),
    },
    {
      title: 'Quyền module',
      key: 'permissions',
      width: 250,
      render: (_: any, record: User) => (
        <Select
          mode="multiple"
          size={isMobile ? 'small' : 'middle'}
          style={{ width: '100%', minWidth: 150 }}
          placeholder="Module"
          value={record.permissions || []}
          disabled={record.role === UserRole.ADMIN}
          onChange={(value) => handlePermissionsChange(record.id, value)}
        >
          <Select.Option value="dashboard">Bảng ĐK</Select.Option>
          <Select.Option value="pages">Pages</Select.Option>
          <Select.Option value="posts">Posts</Select.Option>
          <Select.Option value="auto-post">Auto</Select.Option>
        </Select>
      ),
    },
    {
      title: 'Facebook',
      key: 'facebook',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: User) => (
        record.facebookId ? (
          <Tag color="blue" icon={<SafetyCertificateOutlined />} style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>Đã kết nối</Tag>
        ) : (
          <Tag color="default" style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>Chưa kết nối</Tag>
        )
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 100,
      align: 'center' as const,
      fixed: (isMobile ? 'right' : undefined) as any,
      render: (_: any, record: User) => (
        <Space size="middle">
          <Button size={isMobile ? 'small' : 'middle'} icon={<EditOutlined />} onClick={() => showModal(record)} />
          <Button 
            size={isMobile ? 'small' : 'middle'}
            icon={<DeleteOutlined />} 
            danger 
            onClick={() => handleDelete(record.id)} 
            disabled={record.role === UserRole.ADMIN && users.filter(u => u.role === UserRole.ADMIN).length <= 1}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? '12px' : '0' }}>
      <div style={{ 
        marginBottom: 24, 
        display: 'flex', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 16
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <TeamOutlined style={{ fontSize: isMobile ? 20 : 24, color: '#1890ff' }} />
          <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Người dùng</Title>
        </div>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          block={isMobile}
          onClick={() => showModal()}
        >
          Thêm người dùng
        </Button>
      </div>

      <Card bodyStyle={{ padding: isMobile ? '0' : '24px' }} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Table 
          columns={columns} 
          dataSource={users} 
          rowKey="id" 
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{ 
            pageSize: 10,
            size: isMobile ? 'small' : 'default' as any
          }}
        />
      </Card>

      <Modal
        title={editingUser ? 'Chỉnh sửa người dùng' : 'Thêm người dùng mới'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        okText={editingUser ? 'Cập nhật' : 'Thêm mới'}
        cancelText="Hủy"
        width={isMobile ? '95%' : 600}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="username"
            label="Tên đăng nhập"
            rules={[{ required: true, message: 'Vui lòng nhập tên đăng nhập!' }]}
          >
            <Input disabled={!!editingUser} placeholder="Ví dụ: nhanvien01" />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingUser ? "Mật khẩu mới (Để trống nếu không đổi)" : "Mật khẩu"}
            rules={[{ required: !editingUser, message: 'Vui lòng nhập mật khẩu!' }]}
          >
            <Input.Password placeholder="Tối thiểu 6 ký tự" />
          </Form.Item>

          <Form.Item
            name="confirm"
            label="Xác nhận mật khẩu"
            dependencies={['password']}
            rules={[
              {
                required: !editingUser,
                message: 'Vui lòng xác nhận mật khẩu!',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Mật khẩu xác nhận không khớp!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Nhập lại mật khẩu" />
          </Form.Item>

          <Form.Item
            name="name"
            label="Họ và tên"
            rules={[{ required: true, message: 'Vui lòng nhập họ tên!' }]}
          >
            <Input placeholder="Ví dụ: Nguyễn Văn A" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Email không hợp lệ!' }]}
          >
            <Input placeholder="Ví dụ: user@example.com" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Vai trò"
            rules={[{ required: true, message: 'Vui lòng chọn vai trò!' }]}
          >
            <Select placeholder="Chọn vai trò" onChange={(value) => {
              if (value === UserRole.USER) {
                form.setFieldsValue({ permissions: ALL_PERMISSIONS });
              }
            }}>
              <Select.Option value={UserRole.USER}>Người dùng</Select.Option>
              <Select.Option value={UserRole.ADMIN}>Quản trị viên</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
              name="permissions"
              label="Quyền module"
            >
              <Select mode="multiple" placeholder="Chọn các module được phép truy cập">
                <Select.Option value="dashboard">Bảng điều khiển</Select.Option>
                <Select.Option value="pages">Trang Facebook</Select.Option>
                <Select.Option value="posts">Bài viết</Select.Option>
                <Select.Option value="auto-post">Tự động đăng bài</Select.Option>
              </Select>
            </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
